import {
  capturePageview as adapterPageview,
  aliasIds,
  captureEvent,
  identify,
  pushToDataLayer,
  reset,
  setGroupOrganization,
  setGroupPartner,
} from "./posthog-adapter.js";
import { stripPII } from "./sanitize.js";
import type {
  AnalyticsProps,
  AnalyticsValue,
  IdentitySetOnceProps,
  IdentitySetProps,
  OrganizationContextProps,
  Platform,
} from "./types.js";

let activePlatform: Platform | null = null;

export function __setPlatform(platform: Platform): void {
  activePlatform = platform;
}

export function __getPlatform(): Platform | null {
  return activePlatform;
}

/**
 * Fire an analytics event. Two layers of PII defence apply automatically
 * (call-site `stripPII` + send-time `sanitize_properties`). Tags every event
 * with `app_name` — the property the rest of the org segments apps by in
 * PostHog (cockpit / wallet / registration / partners / census …). PostHog
 * captures also inherit `app_name` as a super-property registered at init
 * (so `$pageview` / `$identify` carry it too); we add it here as well so the
 * GTM dataLayer push — which super-properties don't reach — stays tagged.
 */
export function track(event: string, props: AnalyticsProps = {}): void {
  if (!activePlatform) {
    // Pre-`<AnalyticsProvider>` mount, the wrapper hasn't been initialised
    // yet. Buffering is intentionally NOT done — once the provider mounts,
    // `<RouteTracker>` and `<AnalyticsIdentity>` flush their own first
    // events; `track()` calls earlier than that are almost always a bug.
    return;
  }
  const safeProps = { ...stripPII(props), app_name: activePlatform };
  captureEvent(event, safeProps);
  pushToDataLayer(event, safeProps);
}

/**
 * Identify the current user. Pass the Cognito `sub` (NOT the email) as
 * `userId`. The optional `email` argument is the ONLY sanctioned path
 * for an email to land on a Person profile.
 */
export function identifyUser(
  userId: string,
  setProps?: IdentitySetProps,
  setOnceProps?: IdentitySetOnceProps,
  email?: string,
): void {
  if (!userId) return;
  identify(userId, setProps, setOnceProps, email);
}

/** Attach a PostHog `organization` group to the current user. */
export function setOrganizationContext(
  organizationId: string,
  props: OrganizationContextProps = {},
): void {
  if (!organizationId) return;
  setGroupOrganization(organizationId, props);
}

/** Attach a PostHog `partner` group to the current user. */
export function setPartnerContext(partnerId: string): void {
  if (!partnerId) return;
  setGroupPartner(partnerId);
}

/** Used by Cockpit/Wallet first-login to merge the Registration-app
 * Person profile (keyed by email) with the Cognito-`sub`-keyed one.
 * Wired centrally inside `<AnalyticsIdentity>` — do not call from app code. */
export function aliasUser(emailAlias: string, cognitoSub: string): void {
  if (!emailAlias || !cognitoSub) return;
  aliasIds(emailAlias, cognitoSub);
}

/** Clear the current identity. Called automatically by
 * `<AnalyticsIdentity>` when the user signs out. */
export function resetAnalytics(): void {
  reset();
}

export function trackPageView(path: string, search: string): void {
  adapterPageview(path, search);
  // The PostHog `$pageview` (fired in `adapterPageview`) inherits `app_name`
  // from the super-property; the GTM dataLayer push needs it added explicitly.
  pushToDataLayer("page_view", {
    path,
    search,
    ...(activePlatform ? { app_name: activePlatform } : {}),
  } as Readonly<Record<string, AnalyticsValue>>);
}
